import { useState, useEffect, useCallback } from 'react';
import { useUpdateMigrationContextMutation, useCreatePreflightsMutation } from '../../../api';
import { useNotificationContext } from '../../../providers/NotificationProvider';
import { usePreflightPolling } from './usePreflightPolling';
import { useRecoveryState } from './useRecoveryState';
import { groupComponentsByType } from '../utils/preflightUtils';
import { handleError } from '../../../lib/helpers';

/**
 * Custom hook for PreflightMonitor logic
 */
export const usePreflightMonitor = ({ open, preflightRequests, migrationContext, inspectSourcesMode, onComplete }) => {
    const { notifySuccess, notifyError } = useNotificationContext();

    // RTK Query hooks
    const [updateMigrationContext, { isLoading: isUpdatingContext }] = useUpdateMigrationContextMutation();
    const [createPreflights, { isLoading: isCreating }] = useCreatePreflightsMutation();

    // State management
    const [preflights, setPreflights] = useState([]);
    const [preflightComponents, setPreflightComponents] = useState([]);
    const [allComplete, setAllComplete] = useState(false);
    const [allSuccess, setAllSuccess] = useState(false);
    const [fatalError, setFatalError] = useState(false);
    const [error, setError] = useState(null);
    const [showWarningDescription, setShowWarningDescription] = useState(false);
    const [showFatalErrorDescription, setShowFatalErrorDescription] = useState(false);
    const [currentStep, setCurrentStep] = useState('context');
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [completionNotified, setCompletionNotified] = useState(false);
    const [expandedComponents, setExpandedComponents] = useState(new Set());
    const [selectAll, setSelectAll] = useState(false);
    const [nonErrorsHiddenPerComponent, setNonErrorsHiddenPerComponent] = useState(new Map());

    // Recovery state management
    const { handleErrorWarningRecovery, processAutoRecovery, getRecoveryState, clearRecoveryStates } = useRecoveryState(
        {
            onComponentStateChange: (componentId, changes) => {
                setPreflightComponents((prev) =>
                    prev.map((component) =>
                        component.resource_id === componentId ? { ...component, ...changes } : component,
                    ),
                );

                // Update selection based on recovery
                if (changes.selected !== undefined) {
                    if (changes.selected) {
                        setSelectedRowKeys((prev) => (prev.includes(componentId) ? prev : [...prev, componentId]));
                    } else {
                        setSelectedRowKeys((prev) => prev.filter((id) => id !== componentId));
                    }
                }
            },
        },
    );

    // Handle preflight status updates
    const handleStatusUpdate = useCallback(
        (preflightId, statusData) => {
            setPreflightComponents((prev) => {
                const updated = prev.map((component) =>
                    component.id === preflightId
                        ? {
                              ...component,
                              state: statusData.state,
                              errorType: statusData.errorType,
                              data: statusData.data,
                              progress: statusData.progress,
                          }
                        : component,
                );

                // Auto-expand in source inspection mode
                if (statusData.autoExpand && inspectSourcesMode) {
                    const component = updated.find((c) => c.id === preflightId);
                    if (component) {
                        setExpandedComponents((prev) => new Set(prev).add(component.resource_id));
                    }
                }

                // Check completion status
                const allDone = updated.every((c) => c.state === 'success' || c.state === 'error');
                const allSuccessful = updated.every((c) => c.state === 'success' && !c.errorType);
                const hasFatalError = updated.some((c) => c.state === 'error' && c.errorType !== 'warning');
                const hasWarning = updated.some(
                    (c) =>
                        (c.state === 'success' && c.errorType === 'warning') ||
                        (c.state === 'error' && c.errorType === 'warning'),
                );

                if (allDone !== allComplete) {
                    setAllComplete(allDone);
                    if (allDone) {
                        setAllSuccess(allSuccessful);
                        setFatalError(hasFatalError);
                        setShowWarningDescription(hasWarning);
                        setShowFatalErrorDescription(hasFatalError);
                    }
                }

                return updated;
            });
        },
        [allComplete, inspectSourcesMode],
    );

    // Preflight polling
    const { startPreflightPolling, stopAllPolling } = usePreflightPolling({
        onStatusUpdate: handleStatusUpdate,
        inspectSourcesMode,
        processAutoRecovery: (preflightId, data) => {
            const component = preflightComponents.find((c) => c.id === preflightId);
            if (component) {
                processAutoRecovery(component.resource_id, data);
            }
        },
    });

    // Group components by type
    const groupedComponents = groupComponentsByType(preflightComponents);

    // Start migration process
    const startMigrationProcess = useCallback(async () => {
        try {
            setError(null);
            setCurrentStep('context');

            await updateMigrationContextForSources();
            setCurrentStep('preflights');
            await startPreflights();
        } catch (err) {
            const alternateMsg =
                err.status === 403 ? 'Failed to start preflight.' : 'Failed to start migration process';
            setError(alternateMsg);
            handleError(err, notifyError, alternateMsg);
        }
    }, [preflightRequests, migrationContext, notifyError]);

    const updateMigrationContextForSources = useCallback(async () => {
        const sourceIds = preflightRequests
            .filter((request) => request.type === 'source' || request.type === 'source_discovery')
            .map((request) => request.resource_id);

        if (sourceIds.length === 0) return;

        const updatePromises = sourceIds.map(async (sourceId) => {
            const payload = {
                data_only_migration: migrationContext.dataOnlyMigration || false,
                appliance_id: migrationContext.applianceId || migrationContext.appliance_id,
            };

            if (migrationContext.sourceCredentials?.[sourceId]) {
                payload.credentials = migrationContext.sourceCredentials[sourceId];
            }

            return updateMigrationContext({ sourceId, payload }).unwrap();
        });

        await Promise.all(updatePromises);
    }, [preflightRequests, migrationContext, updateMigrationContext]);

    /**
     * Start preflight checks
     * @param {Array|null} customPreflightRequests - Optional custom preflight requests array for specific operations (e.g., restart a single check).
     *  If provided, only these requests are processed and existing components are updated.
     *  If null, all preflightRequests are processed and components are replaced.
     */
    const startPreflights = useCallback(
        async (customPreflightRequests = null) => {
            const requestsToProcess = customPreflightRequests || preflightRequests;
            const result = await createPreflights(requestsToProcess).unwrap();

            if (result.errors && result.errors.length) {
                throw new Error(result.errors[0]);
            }

            const preflightList = result.preflights || [];

            if (customPreflightRequests) {
                // For restart: only update specific components
                const newComponents = preflightList.map((preflight) => {
                    const request = customPreflightRequests.find((req) => req.resource_id === preflight.resource_id);
                    return {
                        ...preflight,
                        state: 'pending',
                        errorType: null,
                        data: null,
                        type: request?.type || preflight.type,
                        progress: 0,
                        sourceData: request?.sourceData,
                        recovery: [],
                    };
                });

                // Update only the restarted components
                setPreflightComponents((prev) => {
                    const updatedComponents = [...prev];
                    newComponents.forEach((newComponent) => {
                        const index = updatedComponents.findIndex((c) => c.resource_id === newComponent.resource_id);
                        if (index !== -1) {
                            updatedComponents[index] = newComponent;
                        }
                    });
                    return updatedComponents;
                });
            } else {
                setPreflights(preflightList);

                const initialComponents = preflightList.map((preflight) => {
                    const request = preflightRequests?.find((req) => req.resource_id === preflight.resource_id);
                    return {
                        ...preflight,
                        state: 'pending',
                        errorType: null,
                        data: null,
                        type: request?.type || preflight.type,
                        progress: 0,
                        sourceData: request?.sourceData,
                        recovery: [],
                    };
                });
                setPreflightComponents(initialComponents);
            }

            preflightList.forEach((preflight) => {
                startPreflightPolling(preflight);
            });
        },
        [createPreflights, preflightRequests, startPreflightPolling],
    );

    // Reset state when modal closes
    useEffect(() => {
        if (!open) {
            stopAllPolling();
            setPreflights([]);
            setPreflightComponents([]);
            setAllComplete(false);
            setAllSuccess(false);
            setFatalError(false);
            setError(null);
            setShowWarningDescription(false);
            setShowFatalErrorDescription(false);
            setCurrentStep('context');
            setSelectedRowKeys([]);
            setCompletionNotified(false);
            setExpandedComponents(new Set());
            setSelectAll(false);
            setNonErrorsHiddenPerComponent(new Map());
            clearRecoveryStates();
        }
    }, [open, stopAllPolling, clearRecoveryStates]);

    // Start preflights when modal opens
    useEffect(() => {
        if (open && preflightRequests && preflightRequests.length > 0) {
            startMigrationProcess();
        }
    }, [open, preflightRequests, startMigrationProcess]);

    // Check for restart scenarios
    useEffect(() => {
        if (preflightComponents.length > 0) {
            const allDone = preflightComponents.every((c) => c.state === 'success' || c.state === 'error');
            if (allDone !== allComplete) {
                setAllComplete(allDone);
                if (allDone) {
                    const allSuccessful = preflightComponents.every((c) => c.state === 'success' && !c.errorType);
                    const hasFatalError = preflightComponents.some(
                        (c) => c.state === 'error' && c.errorType !== 'warning',
                    );
                    const hasWarning = preflightComponents.some(
                        (c) =>
                            (c.state === 'success' && c.errorType === 'warning') ||
                            (c.state === 'error' && c.errorType === 'warning'),
                    );
                    setAllSuccess(allSuccessful);
                    setFatalError(hasFatalError);
                    setShowWarningDescription(hasWarning);
                    setShowFatalErrorDescription(hasFatalError);
                }
            }
        }
    }, [preflightComponents, allComplete]);

    // Handle restart preflight for a specific resource (reusing startPreflights function)
    const handleRestart = useCallback(
        async (resourceId) => {
            try {
                // Find the original request for this resource
                const originalRequest = preflightRequests.find((req) => req.resource_id === resourceId);
                if (!originalRequest) {
                    notifyError({ description: 'Component not found for restart' });
                    return;
                }

                // Collapse the component first
                setExpandedComponents((prev) => {
                    const newSet = new Set(prev);
                    newSet.delete(resourceId);
                    return newSet;
                });

                // Reuse the startPreflights function with just this one resource
                await startPreflights([originalRequest]);

                notifySuccess({ description: 'Preflight recheck started' });
            } catch (error) {
                console.error('Error restarting preflight:', error);
                notifyError({ description: 'Error restarting source inspection' });

                // Reset component to error state on failure
                setPreflightComponents((prev) =>
                    prev.map((component) =>
                        component.resource_id === resourceId
                            ? {
                                  ...component,
                                  state: 'error',
                                  errorType: 'fatal',
                              }
                            : component,
                    ),
                );

                // Expand the component to show error details when restart fails
                setExpandedComponents((prev) => {
                    const newSet = new Set(prev);
                    newSet.add(resourceId);
                    return newSet;
                });
            }
        },
        [preflightRequests, startPreflights, notifySuccess, notifyError, setExpandedComponents],
    );

    return {
        // State
        preflightComponents,
        groupedComponents,
        allComplete,
        allSuccess,
        fatalError,
        error,
        showWarningDescription,
        showFatalErrorDescription,
        selectedRowKeys,
        expandedComponents,
        selectAll,
        nonErrorsHiddenPerComponent,
        completionNotified,
        isLoading: isUpdatingContext || isCreating,
        currentStep,

        // Actions
        setSelectedRowKeys,
        setExpandedComponents,
        setSelectAll,
        setNonErrorsHiddenPerComponent,
        setCompletionNotified,
        handleErrorWarningRecovery,
        getRecoveryState,

        // Handlers
        handleStatusUpdate,
        handleRestart,
    };
};
